/** How a clause reads on every page: one label, symbol and tone per status, how statuses
 * count up, its shape and who assesses it in words, and where on a page it belongs. */
import type {
  ClauseStanding,
  ItemState,
  GroupStatus,
  LineAssessment,
  LineStatus,
  RegisterGoal,
  RegisterGroup,
  RegisterLine,
  RegisterTopic,
} from "../api";
import { formatDay } from "../utils/attributeFormat";
import styles from "./Clauses.module.css";

export const STATUS_LABEL: Record<GroupStatus, string> = {
  unassessed: "Not assessed",
  meets: "Meets",
  met_by_option: "Met by another option",
  deviates: "Deviates",
  not_applicable: "Not applicable",
  not_evidenced: "Not evidenced",
  not_due: "Not due yet",
  deferred: "Deferred",
  recheck: "Recheck",
  open: "Open",
  met: "Met",
};

/** What a clause's status is when read at a glance, and how it is filtered and counted. */
export type StatusKind =
  | "deviates"
  | "not_evidenced"
  | "open"
  | "deferred"
  | "not_due"
  | "met"
  | "met_by_option"
  | "not_applicable";

export const KIND_OF: Record<GroupStatus, StatusKind> = {
  deviates: "deviates",
  not_evidenced: "not_evidenced",
  unassessed: "open",
  deferred: "deferred",
  recheck: "open",
  open: "open",
  not_due: "not_due",
  meets: "met",
  met: "met",
  met_by_option: "met_by_option",
  not_applicable: "not_applicable",
};

/** In the order a reader works through them: what is wrong or owed first. */
export const STATUS_KINDS: StatusKind[] = [
  "deviates",
  "not_evidenced",
  "open",
  "deferred",
  "not_due",
  "met",
  "met_by_option",
  "not_applicable",
];

/** In the order a summary and its key read: what is met first, then the rest worst first. */
export const SUMMARY_KINDS: StatusKind[] = [
  "met",
  "met_by_option",
  ...STATUS_KINDS.filter((k) => k !== "met" && k !== "met_by_option"),
];

export const KIND_SYMBOL: Record<StatusKind, string> = {
  deviates: "✕",
  not_evidenced: "!",
  open: "○",
  deferred: "‖",
  not_due: "◷",
  met: "✓",
  met_by_option: "(✓)",
  not_applicable: "–",
};

export const KIND_LABEL: Record<StatusKind, string> = {
  deviates: "Deviates",
  not_evidenced: "Not evidenced",
  open: "Not assessed or open",
  deferred: "Deferred",
  not_due: "Not due yet",
  met: "Met",
  met_by_option: "Met by another option",
  not_applicable: "Not applicable",
};

// How many of each reads in a summary line: "1 deviate · 17 not evidenced · 8 met".
const KIND_COUNTED: Record<StatusKind, string> = {
  deviates: "deviate",
  not_evidenced: "not evidenced",
  open: "open",
  deferred: "deferred",
  not_due: "not due yet",
  met: "met",
  met_by_option: "met by another option",
  not_applicable: "not applicable",
};

export type Counts = Partial<Record<GroupStatus, number>>;

export const kindCounts = (counts: Counts): Partial<Record<StatusKind, number>> => {
  const out: Partial<Record<StatusKind, number>> = {};
  for (const [status, n] of Object.entries(counts) as [GroupStatus, number][]) {
    const kind = KIND_OF[status];
    out[kind] = (out[kind] ?? 0) + n;
  }
  return out;
};

export const countStatuses = (statuses: GroupStatus[]): Counts => {
  const out: Counts = {};
  for (const s of statuses) out[s] = (out[s] ?? 0) + 1;
  return out;
};

/** Each kind present with its count, in summary order: "8 met", "1 deviate". */
export const countedKinds = (counts: Counts): [StatusKind, string][] => {
  const kinds = kindCounts(counts);
  return SUMMARY_KINDS.filter((k) => kinds[k]).map((k) => [k, `${kinds[k]} ${KIND_COUNTED[k]}`]);
};

export const statusTone = (status: GroupStatus): string => {
  const kind = KIND_OF[status];
  return kind === "met" || kind === "met_by_option" || kind === "not_applicable"
    ? styles.chipGood
    : kind === "deviates"
      ? styles.chipBad
      : kind === "not_due" || status === "unassessed"
        ? ""
        : styles.chipWarn;
};

const SYMBOL_TONE: Record<StatusKind, string> = {
  deviates: styles.symBad,
  not_evidenced: styles.symWarn,
  open: styles.symMuted,
  deferred: styles.symWarn,
  not_due: styles.symMuted,
  met: styles.symGood,
  met_by_option: styles.symOption,
  not_applicable: styles.symMuted,
};

export const symbolTone = (kind: StatusKind): string => SYMBOL_TONE[kind];

/** When a not-due clause comes back: the stage, the date, or both. */
export const dueText = (assessment?: LineAssessment | null): string =>
  assessment
    ? [
        assessment.due_at ? `at ${assessment.due_at}` : "",
        assessment.due_by ? `by ${formatDay(assessment.due_by)}` : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

/** A status in words: a not-due clause names the stage and the date that bring it back. */
export const statusText = (status: GroupStatus, assessment?: LineAssessment | null): string => {
  const due = status === "not_due" ? dueText(assessment) : "";
  return due ? `${STATUS_LABEL.not_due} · ${due}` : STATUS_LABEL[status];
};

/** Where a clause sits on a project, for one specialist's card: what is wrong or still
 * owed first, then what it has not looked at yet, then what is not due yet, then what is
 * settled. */
export type Standing = "problem" | "unassessed" | "not_due" | "settled";

export const standingOf = (status: LineStatus): Standing => {
  const kind = KIND_OF[status];
  return status === "unassessed"
    ? "unassessed"
    : kind === "not_due"
    ? "not_due"
    : kind === "met" || kind === "met_by_option" || kind === "not_applicable"
      ? "settled"
      : "problem";
};

export const byProblem = (a: LineStatus, b: LineStatus): number =>
  STATUS_KINDS.indexOf(KIND_OF[a]) - STATUS_KINDS.indexOf(KIND_OF[b]);

/** The bands a clause list is read in, beside the checklist of our own obligations. */
export type Band = "dependencies" | "terms" | "principles" | "conflicts" | "awaiting";

export const BAND_TITLE: Record<Band, string> = {
  dependencies: "Dependencies: the client's and third parties' obligations",
  terms: "Commercial terms, mechanisms and boilerplate",
  principles: "Principles and standards of care",
  conflicts: "Contradictions to resolve with the client",
  awaiting: "Awaiting another document",
};

export const BAND_ORDER: Band[] = ["dependencies", "terms", "principles", "conflicts", "awaiting"];

// A restatement is read on its original, so it is no checklist row of its own.
const BAND_OF_STANDING: Record<ClauseStanding, Band | null> = {
  restatement: null,
  dependency: "dependencies",
  term: "terms",
  mechanism: "terms",
  boilerplate: "terms",
  principle: "principles",
  conflict: "conflicts",
  awaiting_document: "awaiting",
  // Read under its list or deliverable, never a row of its own.
  member: null,
  criterion: null,
};

/** Text the reviewer judged no part of anyone's obligations (a page's furniture, a form's
 * blanks): read only in the whole document, with its reason. */
export const isIrrelevant = (line: RegisterLine): boolean =>
  (line.classification as string) === "irrelevant";

/** Where a clause shows on its document's Checklist tab: a row (an obligation its
 * specialists assess), a band, or only in the whole document (headings, definitions,
 * notes, restatements). Off a project nothing is awaiting a document yet, so a clause
 * needing one a clause list could supply goes there; one needing any other document
 * stays in the checklist, its need shown. */
export const placeOf = (line: RegisterLine): Band | "checklist" | null => {
  if (isIrrelevant(line)) return null;
  if (line.standing) return BAND_OF_STANDING[line.standing];
  if (!line.assessable) return null;
  return line.depends_on?.fileable ? "awaiting" : "checklist";
};

const OBLIGOR_LABEL: Record<NonNullable<RegisterLine["obligor"]>, string | null> = {
  provider: null,
  mutual: "Both parties",
  purchaser: "The client's obligation",
  third_party: "A third party's obligation",
};

const KIND_OF_CLAUSE: Record<NonNullable<RegisterLine["kind"]>, string | null> = {
  obligation: null,
  commercial_term: "Commercial term",
  mechanism: "Mechanism",
  boilerplate: "Boilerplate",
  principle: "Principle",
};

/** A clause's whose-and-what in words, where it is not simply our obligation. */
// A list is assessed with its members whatever kind its lead-in got, so no kind badge.
export const shapeLabels = (line: RegisterLine): string[] =>
  [
    line.obligor ? OBLIGOR_LABEL[line.obligor] : null,
    line.kind && line.entry !== "list" ? KIND_OF_CLAUSE[line.kind] : null,
  ].filter((l): l is string => !!l);

/** Who assesses a clause: the specialists its topics route to. */
export const assessedBy = (line: RegisterLine, agentLabel: (agent: string) => string): string =>
  line.assessors.length === 0
    ? "No specialist assesses it yet"
    : // A dependency, term or form is shown to its specialists, never assessed.
      `${line.standing && line.standing !== "restatement" ? "Listed to" : "Assessed by"} ${line.assessors.map(agentLabel).join(" and ")}`;

// A section whose title says it defines terms: an obligation found there is read as
// "the defined term: the obligation".
// Plural only: "5 Definition of main components" is a design section, not a glossary.
const DEFINITIONS_SECTION = /\b(definitions|interpretation)\b/i;
const DEFINITIONS = "definitions";
const DEFINED_TERM = /^\s*(?:\d+(?:\.\d+)*\.?\s+)?["“]([^"”]{1,80})["”]/;

export const inDefinitions = (line: RegisterLine): boolean =>
  DEFINITIONS_SECTION.test(line.section_path[line.section_path.length - 1] ?? "");

/** A clause's row title: a package member's document, or, for an obligation set out in a
 * definition, the term it sits in before it. */
export const rowTitle = (line: RegisterLine): string => {
  if (line.document_title) return line.document_title;
  // A list stated by its heading reads as the obligation, not the heading's label.
  if (line.entry === "list" && line.classification === "heading" && line.statement) {
    return line.statement;
  }
  if (!inDefinitions(line)) return line.title;
  const term = line.text.match(DEFINED_TERM)?.[1] ?? line.definitions[0]?.term;
  return term && !line.title.includes(term) && !line.reference.includes(term)
    ? `${term}: ${line.title}`
    : line.title;
};

// Three-letter words that are words, not acronyms, in a heading printed in capitals.
const COMMON_WORDS = new Set([
  "a", "an", "and", "as", "at", "by", "for", "in", "of", "on", "or", "the", "to",
  "all", "any", "are", "but", "can", "its", "low", "may", "new", "not", "off", "one",
  "our", "out", "own", "per", "see", "set", "top", "two", "use", "via", "way",
]);

/** Kept in capitals: two letters or fewer, three that are not a common word, or a word
 * with no vowel ("PV", "DNO", "CCTV"). */
const isAcronym = (word: string): boolean => {
  const lower = word.toLowerCase();
  return (
    word.length <= 2 ||
    (word.length === 3 && !COMMON_WORDS.has(lower)) ||
    !/[aeiouy]/.test(lower)
  );
};

const sentenceCase = (text: string): string => {
  let first = true;
  return text.replace(/[A-Za-z][A-Za-z'’&]*/g, (word) => {
    const out = isAcronym(word) ? word : word.toLowerCase();
    if (!first) return out;
    first = false;
    return out === word ? out : out[0].toUpperCase() + out.slice(1);
  });
};

/** A heading as a reader would write it: each part printed in capitals (a whole title,
 * or the name after "Exhibit 1.2 –") reads in sentence case, keeping short words that are
 * likely acronyms ("PV plant design requirements"). */
export const sectionTitle = (title: string): string =>
  title
    .split(/(\s[–—-]\s)/)
    .map((part) => {
      const letters = part.replace(/[^A-Za-z]/g, "");
      return letters.length >= 4 && letters === letters.toUpperCase()
        ? sentenceCase(part)
        : part;
    })
    .join("");

/** Two nested headings shown as one header: the one that contains the other, or both. */
export const mergedTitle = (outer: string, inner: string): string => {
  const [a, b] = [outer.toLowerCase(), inner.toLowerCase()];
  if (a.includes(b)) return outer;
  if (b.includes(a)) return inner;
  return `${outer} › ${inner}`;
};

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** A clause's reference without its section heading's words repeated ahead of it
 * ("Commissioning Date" under Definitions, not "Definitions, Commissioning Date"). */
export const shownReference = (line: RegisterLine): string => {
  const heading = line.section_path[line.section_path.length - 1];
  if (!heading) return line.reference;
  const rest = line.reference.replace(
    new RegExp(`^${escapeRegExp(heading)}[\\s,:-]+`, "i"),
    "",
  );
  return rest || line.reference;
};

/** A reference as a reader would cite it: "Exhibit 1.1", not the build's own label for
 * the part of it ("Exhibit 1.1 table lead-in and header"). Stops at the first lowercase
 * word after the first. */
export const citedReference = (reference: string): string => {
  const words = reference.replace(/\s*\(.*$/, "").split(/\s+/).filter(Boolean);
  const end = words.findIndex((w, i) => i > 0 && /^[a-z]/.test(w));
  return (end === -1 ? words : words.slice(0, end)).join(" ");
};

// Words that make a quote a condition rather than a restatement of the goal's title.
const CONDITION = /\b(if|unless|provided|where|only|until|subject to|not later than|within)\b/i;

/** Whether a goal's quote says something its title and due point do not: a condition,
 * or a figure (a date, a period) neither names. */
export const quoteAddsInformation = (goal: RegisterGoal): boolean => {
  if (!goal.quote) return false;
  const said = `${goal.title} ${goal.due}`;
  const figures = goal.quote.match(/\d+(?:[.,]\d+)*/g) ?? [];
  return CONDITION.test(goal.quote) || figures.some((f) => !said.includes(f));
};

/** A goal's own address on its document's page. */
export const goalAnchor = (key: string): string => `goal-${key}`;

/** How a list of clauses is grouped below its goals: the goals the document states
 * first, then each clause's fallback group (its section, or its topic), in ``order``. */
export interface Grouping {
  goals: RegisterGoal[];
  fallbackOf: (line: RegisterLine) => string;
  titleOf: (key: string) => string;
  order: string[];
}

/** The goal or other group a clause is read under: its first goal, else its fallback. */
export const groupOf = (line: RegisterLine, grouping: Grouping): string =>
  line.goal_keys.find((k) => grouping.goals.some((g) => g.key === k)) ??
  grouping.fallbackOf(line);

const DEFINITIONS_TITLE = "Obligations set out in the definitions";

/** By section, as the document prints it: a file's own page. */
export const sectionGrouping = (goals: RegisterGoal[], groups: RegisterGroup[]): Grouping => {
  const titles = new Map(
    groups.map((g) => [
      g.group_key,
      (g.section_path.length > 0 ? g.section_path : [g.title]).map(sectionTitle).join(" › "),
    ]),
  );
  return {
    goals,
    fallbackOf: (line) => (inDefinitions(line) ? DEFINITIONS : line.group_key),
    titleOf: (key) =>
      key === DEFINITIONS ? DEFINITIONS_TITLE : (titles.get(key) ?? "Before the first heading"),
    order: [...[...groups].sort((a, b) => a.position - b.position).map((g) => g.group_key), DEFINITIONS],
  };
};

/** By topic: a specialist's card, where the document's sections are a reference on each
 * row rather than the way in. Each clause sits under the first of its topics the
 * specialist assesses, not a topic it is only tagged with. */
export const topicGrouping = (
  goals: RegisterGoal[],
  topics: RegisterTopic[],
  agent: string,
): Grouping => ({
  goals,
  fallbackOf: (line) =>
    inDefinitions(line)
      ? DEFINITIONS
      : (line.topics.find((t) => topics.some((x) => x.key === t && x.assessor === agent)) ??
        line.topics[0] ??
        "none"),
  titleOf: (key) =>
    key === DEFINITIONS
      ? DEFINITIONS_TITLE
      : (topics.find((t) => t.key === key)?.label ?? "No topic"),
  order: [...topics.map((t) => t.key), "none", DEFINITIONS],
});

/** A list member's state on its list's assessment, at a glance. */
export type ItemKind = ItemState | "unstated";

export const ITEM_SYMBOL: Record<ItemKind, string> = {
  provided: "✓",
  not_applicable: "–",
  missing: "!",
  deficient: "✕",
  unstated: "○",
};

export const ITEM_LABEL: Record<ItemKind, string> = {
  provided: "Provided",
  not_applicable: "Not applicable",
  missing: "Missing",
  deficient: "Deficient",
  unstated: "Not stated yet",
};

const ITEM_TONE: Record<ItemKind, string> = {
  provided: styles.symGood,
  not_applicable: styles.symMuted,
  missing: styles.symWarn,
  deficient: styles.symBad,
  unstated: styles.symMuted,
};

export const itemTone = (kind: ItemKind): string => ITEM_TONE[kind];

/** A list's members by state, as a line: "1 of 3 provided · 1 missing · 1 deficient". */
export const itemCountsText = (
  counts: Record<ItemKind, number> & { total: number },
): string =>
  [
    `${counts.provided} of ${counts.total} provided`,
    counts.not_applicable ? `${counts.not_applicable} n/a` : "",
    counts.missing ? `${counts.missing} missing` : "",
    counts.deficient ? `${counts.deficient} deficient` : "",
    counts.unstated ? `${counts.unstated} not stated` : "",
  ]
    .filter(Boolean)
    .join(" · ");

/** The package or list goal a list entry's members make up, for the gaps in its numbering. */
export const listGoalOf = (line: RegisterLine, goals: RegisterGoal[]): RegisterGoal | null => {
  const ids = new Set(line.parts.map((p) => p.line_id));
  return goals.find((g) => g.kind !== "gate" && g.line_ids.some((id) => ids.has(id))) ?? null;
};
